import { cn, controlClasses, Select } from '@fruuit/ui'
import { Search, X } from 'lucide-react'
import { Link } from 'react-router'
import type { HelpMatch } from './search'
import { headingsOf } from './search'
import type { HelpSection } from './types'

/** `/help` for the welcome section, `/help/<id>` otherwise. */
export function sectionPath(section: HelpSection, hash?: string): string {
  const base = section.id === 'welcome' ? '/help' : `/help/${section.id}`
  return hash ? `${base}#${hash}` : base
}

interface FilterProps {
  query: string
  onQuery: (value: string) => void
  id: string
}

/** Filter box shared by the desktop contents and the mobile bar. */
export function HelpFilter({ query, onQuery, id }: FilterProps) {
  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">
        Search help
      </label>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-sage"
      />
      <input
        id={id}
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder="Search help"
        autoComplete="off"
        className={cn(controlClasses, 'h-10 pr-9 pl-9 [&::-webkit-search-cancel-button]:hidden')}
      />
      {query ? (
        <button
          type="button"
          onClick={() => onQuery('')}
          aria-label="Clear search"
          className="absolute top-1/2 right-2 grid size-6 -translate-y-1/2 place-items-center rounded-sm text-sage hover:bg-wash hover:text-ink focus-visible:outline-2 focus-visible:outline-teal"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      ) : null}
    </div>
  )
}

interface TocProps {
  matches: HelpMatch[]
  /** The section being read (its sub-headings are listed), or null on the home page. */
  current: HelpSection | null
  query: string
  onQuery: (value: string) => void
  total: number
}

/** Desktop contents: sticky, with the search on top. Sub-headings show for the open section or for matches. */
export function HelpToc({ matches, current, query, onQuery, total }: TocProps) {
  return (
    <nav aria-label="Help contents" className="flex flex-col gap-4">
      <HelpFilter query={query} onQuery={onQuery} id="help-filter" />
      <p className="px-1 text-xs text-sage" aria-live="polite">
        {query ? `${matches.length} of ${total} sections match` : `${total} sections`}
      </p>
      <ol className="flex flex-col gap-0.5">
        {matches.map(({ section, headings, inHeadings }) => {
          const Icon = section.icon
          const active = current?.id === section.id
          const subheadings = query
            ? headings
            : active
              ? headingsOf(section).filter((heading) => heading.kind === 'h')
              : []
          return (
            <li key={section.id}>
              <Link
                to={sectionPath(section)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'group flex items-start gap-2.5 rounded-md px-2.5 py-1.5 text-sm leading-snug no-underline transition-colors',
                  active
                    ? 'bg-teal-50 font-semibold text-teal-800'
                    : 'text-ink-soft hover:bg-wash hover:text-ink',
                )}
              >
                <Icon
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 size-4 shrink-0',
                    active ? 'text-teal-500' : 'text-sage group-hover:text-teal-500',
                  )}
                />
                <span className="min-w-0">
                  {section.title}
                  {query && !inHeadings ? (
                    <span className="block text-[11px] font-normal text-sage">match in text</span>
                  ) : null}
                </span>
              </Link>
              {subheadings.length > 0 ? (
                <ol className="mt-0.5 mb-1.5 ml-[1.35rem] flex flex-col gap-px border-l border-line-soft pl-3">
                  {subheadings.map((heading) => (
                    <li key={heading.id}>
                      <Link
                        to={sectionPath(section, heading.id)}
                        className="block rounded-sm px-1.5 py-1 text-[13px] leading-snug text-ink-soft no-underline hover:bg-wash hover:text-ink"
                      >
                        {heading.text}
                      </Link>
                    </li>
                  ))}
                </ol>
              ) : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

interface MobileProps {
  matches: HelpMatch[]
  current: HelpSection | null
  query: string
  onQuery: (value: string) => void
  onJump: (path: string) => void
}

/** Below 1024 px: the search plus a "Go to" select instead of the side list. */
export function HelpMobileNav({ matches, current, query, onQuery, onJump }: MobileProps) {
  const options = matches.flatMap(({ section, headings }) => [
    { value: sectionPath(section), label: section.title },
    ...headings.map((heading) => ({
      value: sectionPath(section, heading.id),
      label: `   ${section.title} › ${heading.text}`,
    })),
  ])
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4">
      <Select
        label="Go to section"
        value=""
        placeholder={
          matches.length
            ? current
              ? `Reading: ${current.title}`
              : 'Choose a section…'
            : 'Nothing matches the search'
        }
        options={options}
        onChange={(event) => {
          if (event.target.value) onJump(event.target.value)
        }}
      />
      <HelpFilter query={query} onQuery={onQuery} id="help-filter-mobile" />
    </div>
  )
}
